/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Highlight } from 'ui/atoms/Highlight/Highlight';
import { Section } from 'ui/molecules/Section/Section';

import styles from './EnrichmentMatchingSection.module.scss';

const IDENTIFIERS = [
  { label: 'Registration number', matchLabel: 'registration number', example: '08804411' },
  { label: 'VAT number', matchLabel: 'VAT number', example: 'GB260423730' },
  { label: 'Website or domain', matchLabel: 'domain', example: 'revolut.com' },
  { label: 'LinkedIn URL', matchLabel: 'LinkedIn URL', example: 'linkedin.com/company/revolut' },
  { label: 'Work email', matchLabel: 'work email', example: 'name@revolut.com' },
  { label: 'Ticker', matchLabel: 'ticker', example: 'MC.PA' },
  { label: 'Company name + country', matchLabel: 'name + country', example: 'Revolut · United Kingdom' },
] as const;

const OTHER_CANDIDATES = [
  { rank: 2, name: 'Revolut NewCo UK Ltd', meta: '13082874 · Companies House', evidence: 'matched on name', score: 71 },
  {
    rank: 3,
    name: 'Revolut Payments UK Ltd',
    meta: '13271327 · Companies House',
    evidence: 'matched on name',
    score: 64,
  },
  {
    rank: 4,
    name: 'Revolut Group Holdings Ltd',
    meta: '13429218 · Companies House',
    evidence: 'matched on address',
    score: 52,
  },
  {
    rank: 5,
    name: 'Revolut Travel Ltd',
    meta: '13598204 · Companies House',
    evidence: 'matched on domain',
    score: 41,
  },
] as const;

const AUTOPLAY_MS = 2400;

export const EnrichmentMatchingSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.25 });

  const [revealed, setRevealed] = useState(false);
  const [active, setActive] = useState(0);
  const [userTook, setUserTook] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    if (inView) setRevealed(true);
  }, [inView]);

  const autoPlaying = inView && !userTook && !reduced;

  useEffect(() => {
    if (!autoPlaying) return undefined;

    const timer = setTimeout(() => {
      setActive((prev) => (prev + 1) % IDENTIFIERS.length);
    }, AUTOPLAY_MS);

    return () => clearTimeout(timer);
  }, [autoPlaying, active]);

  const select = (index: number) => {
    setUserTook(true);
    setActive(index);
  };

  const activeIdentifier = IDENTIFIERS[active];

  return (
    <Section
      fullWidth
      background="#F6F7F9"
      extra={
        <Highlight color="blue" font="mono" uppercase>
          Matching
        </Highlight>
      }
      title="Match on any identifier you have"
      description="No cleanup first. Any one of these seven resolves to a single registry record."
    >
      <div ref={ref} className={cn(styles.root, revealed && styles.revealed)}>
        <div className={styles.attrs} role="tablist" aria-label="Match identifiers">
          {IDENTIFIERS.map((item, i) => {
            const on = i === active;

            return (
              <button
                key={item.label}
                type="button"
                role="tab"
                aria-selected={on}
                className={cn(styles.attr, on && styles.attrActive)}
                style={{ transitionDelay: `${i * 60}ms` }}
                onClick={() => select(i)}
              >
                <b>{item.label}</b>
                <code>{item.example}</code>
              </button>
            );
          })}
        </div>

        <div className={styles.panel}>
          <p className={styles.note}>
            When an input is ambiguous, you get ranked candidates with the evidence behind each one. Your team confirms
            the edge case instead of guessing at it.
          </p>

          <div className={styles.cands}>
            <div className={cn(styles.cand, styles.candBest)}>
              <span className={styles.rank}>1</span>

              <span className={styles.co}>
                <b>Revolut Ltd</b>
                <i>08804411 · Companies House</i>
              </span>

              <span key={active} className={cn(styles.ev, styles.evLive)}>
                matched on {activeIdentifier.matchLabel}
              </span>

              <span className={styles.scoreWrap}>
                <span className={styles.bar}>
                  <span className={cn(styles.barFill, styles.barFillBest)} style={{ width: revealed ? '98%' : 0 }} />
                </span>
                <em className={styles.pct}>98%</em>
              </span>
            </div>

            {OTHER_CANDIDATES.map((c, i) => (
              <div key={c.rank} className={styles.cand} style={{ transitionDelay: `${300 + i * 140}ms` }}>
                <span className={styles.rank}>{c.rank}</span>

                <span className={styles.co}>
                  <b>{c.name}</b>
                  <i>{c.meta}</i>
                </span>

                <span className={styles.ev}>{c.evidence}</span>

                <span className={styles.scoreWrap}>
                  <span className={styles.bar}>
                    <span className={styles.barFill} style={{ width: revealed ? `${c.score}%` : 0 }} />
                  </span>
                  <em className={styles.pct}>{c.score}%</em>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
